import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MailCheck } from 'lucide-react';
import AuthShell from '../components/layout/AuthShell';
import Button from '../components/ui/Button';
import { useAuth, getErrorMessage } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';

const LEN = 6;

export default function VerifyEmail() {
  const { verifyOtp, resendOtp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [digits, setDigits] = useState(Array(LEN).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  // No email in route state → nothing to verify.
  useEffect(() => {
    if (!email) navigate('/signup', { replace: true });
    else inputs.current[0]?.focus();
  }, [email, navigate]);

  // If the server returned a dev code (no email provider configured), prefill it.
  useEffect(() => {
    const dev = location.state?.devCode;
    if (dev && /^\d{6}$/.test(dev)) {
      setDigits(dev.split(''));
      toast.info('Dev mode: code prefilled from the server console.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const setDigit = (i, val) => {
    const v = val.replace(/\D/g, '');
    setDigits((d) => {
      const next = [...d];
      next[i] = v.slice(-1);
      return next;
    });
    if (v && i < LEN - 1) inputs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LEN);
    if (!text) return;
    e.preventDefault();
    setDigits(Array.from({ length: LEN }, (_, i) => text[i] || ''));
    inputs.current[Math.min(text.length, LEN - 1)]?.focus();
  };

  const submit = async (codeArg) => {
    const code = codeArg || digits.join('');
    if (code.length !== LEN) return toast.error('Enter the 6-digit code.');
    setSubmitting(true);
    try {
      await verifyOtp(email, code);
      toast.success('Email verified — welcome to Aether!');
      navigate('/app', { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Verification failed.'));
      setDigits(Array(LEN).fill(''));
      inputs.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit once all six are filled.
  useEffect(() => {
    const code = digits.join('');
    if (code.length === LEN && !submitting) submit(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  const resend = async () => {
    setCooldown(30);
    try {
      const res = await resendOtp(email);
      if (res?.devCode) {
        setDigits(res.devCode.split(''));
        toast.info('Dev mode: new code prefilled.');
      } else {
        toast.success('A new code is on its way.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <AuthShell
      title="Verify your email"
      subtitle={email ? `We sent a 6-digit code to ${email}.` : 'Check your inbox for a code.'}
      footer={
        <>
          Wrong email?{' '}
          <Link to="/signup" className="font-medium text-brand-300 hover:text-brand-200">
            Start over
          </Link>
        </>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center"
      >
        <div className="mb-6 inline-flex rounded-2xl bg-brand-500/15 p-3">
          <MailCheck className="h-6 w-6 text-brand-300" />
        </div>

        <div className="flex gap-2 sm:gap-3" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              inputMode="numeric"
              maxLength={1}
              aria-label={`Digit ${i + 1}`}
              className="glass h-14 w-11 rounded-xl text-center text-2xl font-semibold text-content focus-visible:ring-2 focus-visible:ring-brand-400 sm:w-12"
            />
          ))}
        </div>

        <Button
          className="mt-7 w-full"
          size="lg"
          loading={submitting}
          onClick={() => submit()}
          disabled={digits.join('').length !== LEN}
        >
          Verify
        </Button>

        <button
          onClick={resend}
          disabled={cooldown > 0}
          className="mt-4 text-sm text-content-muted transition-colors hover:text-content disabled:opacity-50"
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
        </button>
      </motion.div>
    </AuthShell>
  );
}
